import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { User as UserIcon, Phone, Wallet, Camera, Award, Zap, MessageSquare, Gift, Copy } from "lucide-react";

const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const TIER_COLORS = {
  bronze: "from-orange-700 to-orange-500",
  silver: "from-slate-500 to-slate-300",
  gold:   "from-[#D4AF37] to-yellow-300",
};

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const { t } = useLang();
  const [form, setForm] = useState({
    real_name: user?.real_name || "",
    nickname:  user?.nickname  || "",
    gender:    user?.gender    || "",
    country_name: user?.country_name || "",
    phone:     user?.phone     || "",
  });
  const [zain, setZain] = useState(user?.zaincash_number || "");
  const [sqi, setSqi]   = useState(user?.super_qi_number || "");
  const [country, setCountry] = useState(user?.country === "IQ" ? "IQ" : user?.country === "INTL" ? "INTL" : "");
  const [picBusy, setPicBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msgForm, setMsgForm] = useState({ open: false, subject: "", body: "" });

  const sendMessageToAdmin = async () => {
    if (!msgForm.subject.trim() || !msgForm.body.trim()) { toast.error("اكتب الموضوع والنص"); return; }
    try {
      await api.post("/user/message-admin", { subject: msgForm.subject, body: msgForm.body });
      toast.success("✓ تم إرسال رسالتك للأدمن");
      setMsgForm({ open: false, subject: "", body: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const saveProfile = async () => {
    setLoading(true);
    try {
      await api.post("/user/profile", form);
      toast.success("✓ تم الحفظ");
      await refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const saveWallet = async () => {
    try {
      await api.post("/user/wallet-numbers", { zaincash_number: zain, super_qi_number: sqi, country: country || undefined });
      toast.success("✓"); await refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const setCountryNow = async (c) => {
    try {
      await api.post("/user/wallet-numbers", { country: c });
      setCountry(c); toast.success("✓"); await refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const uploadPicture = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("صورة فقط"); return; }
    if (f.size > 3 * 1024 * 1024) { toast.error("الحجم > 3MB"); return; }
    setPicBusy(true);
    try {
      const b64 = await readAsBase64(f);
      await api.post("/user/profile", { picture_base64: b64 });
      toast.success("✓ تم تحديث الصورة"); await refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setPicBusy(false); }
  };

  const tier = user?.tier || "bronze";
  const tierLabel = user?.tier_label_ar || "برونزي";
  const xp = user?.xp || 0;
  const nextXp = user?.next_tier_xp || xp;
  const progress = nextXp > xp ? Math.min(100, (xp / nextXp) * 100) : 100;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-3xl sm:text-4xl font-bold text-cyan-gradient mb-2 flex items-center gap-3">
        <UserIcon className="w-7 h-7" /> {t("profile")}
      </h1>
      <p className="text-sm text-[#8FA3BD] mb-6">{user?.email}</p>

      {/* Profile Card */}
      <div className="bento-card p-6 mb-5">
        <div className="flex items-start gap-5">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-[#1A2D4D] overflow-hidden flex items-center justify-center border-2 border-[#00E5FF]/30">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <UserIcon className="w-10 h-10 text-[#8FA3BD]" />
              )}
            </div>
            <label className="absolute -bottom-1 -end-1 cursor-pointer">
              <input type="file" accept="image/*" className="hidden" onChange={uploadPicture} data-testid="profile-pic-input" disabled={picBusy} />
              <span className="w-8 h-8 rounded-full bg-[#00E5FF] text-[#050E1A] flex items-center justify-center"><Camera className="w-4 h-4" /></span>
            </label>
          </div>
          <div className="flex-1">
            <div className="text-lg font-bold">{user?.nickname || user?.name}</div>
            {user?.real_name && <div className="text-xs text-[#8FA3BD]">{user.real_name}</div>}
            <div className={`mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r ${TIER_COLORS[tier]} text-[#050E1A] text-xs font-bold`}>
              <Award className="w-3 h-3" /> {tierLabel}
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs">
              <Zap className="w-3 h-3 text-[#FF9500]" />
              <span className="text-[#FF9500] font-bold">{xp.toLocaleString()}</span>
              <span className="text-[#8FA3BD]">/ {nextXp.toLocaleString()} XP</span>
            </div>
            <div className="xp-bar mt-1.5"><div className="xp-bar-fill" style={{ width: `${progress}%` }} /></div>
          </div>
        </div>
      </div>

      {/* Personal Info */}
      <div className="bento-card p-6 mb-5">
        <h3 className="text-lg font-semibold mb-4">المعلومات الشخصية</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs text-[#8FA3BD]">الاسم الحقيقي</Label>
            <Input value={form.real_name} onChange={(e) => setForm({ ...form, real_name: e.target.value })} className="bg-[#050E1A] border-[#1A2D4D] mt-1" data-testid="real-name-input" /></div>
          <div><Label className="text-xs text-[#8FA3BD]">الاسم المستعار</Label>
            <Input value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} className="bg-[#050E1A] border-[#1A2D4D] mt-1" data-testid="nickname-input" /></div>
          <div>
            <Label className="text-xs text-[#8FA3BD]">الجنس</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button type="button" onClick={() => setForm({ ...form, gender: "male" })}
                className={`p-2.5 rounded-lg border text-sm ${form.gender === "male" ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-[#1A2D4D] text-[#8FA3BD]"}`}
                data-testid="gender-male">👨 ولد</button>
              <button type="button" onClick={() => setForm({ ...form, gender: "female" })}
                className={`p-2.5 rounded-lg border text-sm ${form.gender === "female" ? "border-[#FF69B4] bg-[#FF69B4]/10 text-[#FF69B4]" : "border-[#1A2D4D] text-[#8FA3BD]"}`}
                data-testid="gender-female">👩 بنت</button>
            </div>
          </div>
          <div><Label className="text-xs text-[#8FA3BD]">الدولة</Label>
            <Input value={form.country_name} onChange={(e) => setForm({ ...form, country_name: e.target.value })} placeholder="مثلاً: العراق / مصر / السعودية" className="bg-[#050E1A] border-[#1A2D4D] mt-1" data-testid="country-name-input" /></div>
          <div className="sm:col-span-2"><Label className="text-xs text-[#8FA3BD]">رقم الهاتف</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+9647xxxxxxxxx" className="bg-[#050E1A] border-[#1A2D4D] mt-1" data-testid="phone-input" /></div>
        </div>
        <Button onClick={saveProfile} disabled={loading} className="btn-cyan w-full mt-5" data-testid="save-profile-btn">{loading ? "..." : "حفظ"}</Button>
      </div>

      {/* Referral / Affiliate */}
      <div className="bento-card p-6 mb-5">
        <div className="flex items-center gap-3 mb-3">
          <Gift className="w-5 h-5 text-[#D4AF37]" />
          <h3 className="text-lg font-semibold">🎁 نظام الإحالة المزدوج</h3>
        </div>
        <p className="text-xs text-[#8FA3BD] mb-4">
          ادعُ أصدقاءك بالكود أو الرابط. <span className="text-[#00E676] font-bold">تحصل على 10%</span> من أول إيداع لهم،
          و<span className="text-[#00E5FF] font-bold">يحصلون على 5%</span> بونص ترحيبي على أول إيداع لهم.
        </p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-[#8FA3BD]">كود الإحالة</Label>
            <div className="flex gap-2 mt-1">
              <Input readOnly value={user?.referral_code || ""} className="bg-[#050E1A] border-[#1A2D4D] font-mono text-[#00E5FF]" data-testid="referral-code-display" />
              <Button variant="outline" className="border-[#1A2D4D] text-white" data-testid="copy-ref-code"
                onClick={() => { navigator.clipboard.writeText(user?.referral_code || ""); toast.success("✓ نُسخ الكود"); }}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-[#8FA3BD]">رابط الدعوة المباشر</Label>
            <div className="flex gap-2 mt-1">
              <Input readOnly value={`${window.location.origin}/ref/${user?.referral_code || ""}`}
                className="bg-[#050E1A] border-[#1A2D4D] text-xs" data-testid="referral-link-display" />
              <Button variant="outline" className="border-[#1A2D4D] text-white" data-testid="copy-ref-link"
                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/ref/${user?.referral_code || ""}`); toast.success("✓ نُسخ الرابط"); }}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <Button className="btn-cyan w-full" data-testid="share-ref-btn"
            onClick={async () => {
              const url = `${window.location.origin}/ref/${user?.referral_code || ""}`;
              const text = `🎁 انضم إلى SULFA — جمعيات تكافل اجتماعي. سجّل عبر الرابط واحصل على 5% بونص على أول إيداع: ${url}`;
              if (navigator.share) {
                try { await navigator.share({ title: "SULFA", text, url }); }
                catch { /* user cancelled */ }
              } else {
                navigator.clipboard.writeText(text);
                toast.success("✓ نُسخت رسالة الدعوة");
              }
            }}>
            <Gift className="w-4 h-4 me-1.5" /> مشاركة الدعوة
          </Button>
        </div>
      </div>

      {/* Region & Wallet Numbers */}
      <div className="bento-card p-6 mb-5">
        <div className="flex items-center gap-3 mb-3">
          <Phone className="w-5 h-5 text-[#00E5FF]" />
          <h3 className="text-lg font-semibold">المنطقة وأرقام المحفظة</h3>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button type="button" onClick={() => setCountryNow("IQ")}
            className={`p-3 rounded-lg border text-sm ${user?.country === "IQ" ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-[#1A2D4D] text-[#8FA3BD]"}`}
            data-testid="profile-country-iq">🇮🇶 العراق</button>
          <button type="button" onClick={() => setCountryNow("INTL")}
            className={`p-3 rounded-lg border text-sm ${user?.country === "INTL" ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-[#1A2D4D] text-[#8FA3BD]"}`}
            data-testid="profile-country-intl">🌍 International</button>
        </div>
        <div className="space-y-3">
          <div><Label className="text-xs text-[#8FA3BD]">رقم زين كاش (للسحب)</Label>
            <Input value={zain} onChange={(e) => setZain(e.target.value)} placeholder="07XXXXXXXXX" className="bg-[#050E1A] border-[#1A2D4D] mt-1" data-testid="zaincash-input" /></div>
          <div><Label className="text-xs text-[#8FA3BD]">رقم سوبر كي (للسحب)</Label>
            <Input value={sqi} onChange={(e) => setSqi(e.target.value)} placeholder="07XXXXXXXXX أو كارت" className="bg-[#050E1A] border-[#1A2D4D] mt-1" data-testid="superqi-input" /></div>
          <Button onClick={saveWallet} variant="outline" className="border-[#1A2D4D] text-white w-full" data-testid="save-wallet-btn"><Wallet className="w-4 h-4 me-1.5" />حفظ أرقام المحفظة</Button>
        </div>
      </div>

      {/* Send Message to Admin */}
      <div className="bento-card p-6 mb-5">
        <div className="flex items-center gap-3 mb-3">
          <MessageSquare className="w-5 h-5 text-[#FF9500]" />
          <h3 className="text-lg font-semibold">📨 رسالة إلى الأدمن</h3>
        </div>
        <p className="text-xs text-[#8FA3BD] mb-3">إذا احتجت أي شيء أو لديك استفسار، أرسل رسالة وسيرد عليك الأدمن.</p>
        {!msgForm.open ? (
          <Button variant="outline" className="border-[#1A2D4D] text-white w-full" onClick={() => setMsgForm({ ...msgForm, open: true })} data-testid="open-msg-admin">
            <MessageSquare className="w-4 h-4 me-1.5" /> اكتب رسالة
          </Button>
        ) : (
          <div className="space-y-2">
            <Input value={msgForm.subject} onChange={(e) => setMsgForm({ ...msgForm, subject: e.target.value })} placeholder="الموضوع (مثلاً: استفسار عن السحب)" className="bg-[#050E1A] border-[#1A2D4D]" data-testid="msg-subject" />
            <textarea value={msgForm.body} onChange={(e) => setMsgForm({ ...msgForm, body: e.target.value })} placeholder="اكتب رسالتك..." className="w-full p-3 rounded-lg bg-[#050E1A] border border-[#1A2D4D] text-white text-sm min-h-[100px] outline-none focus:border-[#00E5FF]" data-testid="msg-body" />
            <div className="flex gap-2">
              <Button onClick={sendMessageToAdmin} className="btn-cyan flex-1" data-testid="send-msg-admin">إرسال</Button>
              <Button variant="outline" className="border-[#1A2D4D] text-white" onClick={() => setMsgForm({ open: false, subject: "", body: "" })}>إلغاء</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
